<template>
    <div class="ml-editor">
        <p class="entity-type--helptext">
          {{ $t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.title') }}
        </p>
        <or-textbox
            v-model="machineLearningEntity.name"
            :label="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.name.label')"
            :placeholder="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.name.placeholder')"
            :maxlength="maxNameLength"
            :error="$props.errors.name"
            :invalid="!!$props.errors.name"
            :disabled="isLoading"
            enforceMaxlength
        />

        <or-chips
            :label="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.roles.label')"
            :helpText="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.roles.helpText')"
            :value="machineLearningEntity.roles"
            :simpleText="true"
            :disabled="isLoading"
            @input="updateRoles"
            :error="$props.errors.roles"
            :invalid="!!$props.errors.roles"
        />

        <or-select
            v-if="machineLearningEntity.children.length === 0"
            v-model="localFeatures"
            :label="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.feature.label')"
            :placeholder="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.feature.placeholder')"
            :loading="status.fetching"
            :options="featureOptions"
            multiple
        />
        <div>
            <ml-entity-child
                v-for="(child, index) in machineLearningEntity.children"
                :key="index"
                :name.sync="child.name"
                :features.sync="child.features"
                :children.sync="child.children"
                :entitiesOptions="entitiesOptions"
                :taken-names="getTakenNames(index)"
                :invalid="invalid"
                :error-message="errorMessage"
                @delete="deleteChild(index)"
            />
        </div>
        <or-button
            type="secondary"
            color="primary"
            icon="add"
            size="small"
            class="flat create-btn"
            disabledRipple
            :disabled="invalid"
            @click="addNewChildEntity"
        >
            {{ machineLearningEntity.name
              ? $t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.addChildEntityButton', { mlEntityName: machineLearningEntity.name })
              : $t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.addChildEntityButtonEmpty') }}
        </or-button>
    </div>
</template>

<script>
import _get from 'lodash/get'
import { mapActions, mapState } from 'vuex'
import { entitiesLimits } from '@/modules/limits'
import { getDepth, maxCountOfChildren } from '@/utils/utils'
import { ENTITY_TYPE, getEntityTypeLabel } from '../../entity-types'
import MlEntityChild, { getDefaultChild } from './ml-entity-child'

const FIELD_NAME_FOR_VALIDATION = 'children'
const SUB_ENTITIES_DEPTH = 'ENTITIES.ML.SUB_ENTITIES_DEPTH'
const SUB_ENTITIES_CHILDREN_LENGTH = 'ENTITIES.ML.SUB_ENTITIES_CHILDREN_LENGTH'

export const getDefaultMlEntity = () => ({
  name: '',
  roles: [],
  children: [],
  features: [],
  type: ENTITY_TYPE.machineLearning
})
export default {
  components: {
    MlEntityChild
  },

  data () {
    return {
      maximumAllowedChildren: 20,
      maximumAllowedDepth: 4
    }
  },

  async created () {
    await this.getLimits(SUB_ENTITIES_DEPTH)
    await this.getLimits(SUB_ENTITIES_CHILDREN_LENGTH)

    this.maximumAllowedChildren = _get(this.limits, SUB_ENTITIES_CHILDREN_LENGTH)
    this.maximumAllowedDepth = _get(this.limits, SUB_ENTITIES_DEPTH)
  },

  props: {
    entity: Object,
    isLoading: Boolean,
    errors: Object
  },

  mounted () {
    this.fetchFeatures()
  },

  computed: {
    ...mapState('knowledgebase/features', ['features', 'status']),
    ...mapState('knowledgebase/entities', ['allEntities']),
    ...mapState('knowledgebase', ['limits']),

    maxNameLength () {
      return entitiesLimits.simple.name.max
    },

    machineLearningEntity: {
      get () {
        return this.$props.entity
      },
      set (value) {
        this.$emit('update:entity', value)
      }
    },

    entitiesOptions () {
      return this.allEntities.value
        .filter(entity => entity.id !== this.machineLearningEntity.id)
        .map(entity => ({
          label: entity.name,
          value: entity.name,
          image: getEntityTypeLabel(entity.type)
        }))
    },

    featureOptions () {
      return this.features.map(feature => ({
        label: feature.name,
        value: feature.name
      }))
    },

    localFeatures: {
      get () {
        return this.entity.features
          .filter(modeOrFeature => !!modeOrFeature.featureName)
          .map(feature => feature.featureName)
      },
      set (features) {
        const requiredFeature = this.entity.features
          .find(modeOrFeature => _.has(modeOrFeature, 'featureName') && modeOrFeature.isRequired)
        const requiredFeatureName = requiredFeature ? requiredFeature.featureName : null
        const entities = this.entity.features.filter(modeOrFeature => _.has(modeOrFeature, 'modelName'))

        this.$emit('update:entity', {
          ...this.entity,
          features: [
            ...entities,
            ...features.map(featureName => ({
              featureName: featureName,
              isRequired: featureName === requiredFeatureName
            }))
          ]
        })
      }
    },

    isValidCountOfChildren () {
      return maxCountOfChildren(this.machineLearningEntity.children, FIELD_NAME_FOR_VALIDATION) <= this.maximumAllowedChildren
    },

    isValidMaxDepth () {
      return getDepth(this.machineLearningEntity.children, FIELD_NAME_FOR_VALIDATION) <= this.maximumAllowedDepth
    },

    invalid () {
      return !this.isValidCountOfChildren || !this.isValidMaxDepth
    },

    errorMessage () {
      if (!this.isValidCountOfChildren) {
        return this.$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.maximumChildrenError', {
          maximumAllowedChildren: this.maximumAllowedChildren
        })
      } else if (!this.isValidMaxDepth) {
        return this.$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-editor.maxDepthLevelError', {
          maximumAllowedDepth: this.maximumAllowedDepth
        })
      } else return ''
    }
  },

  methods: {
    ...mapActions('knowledgebase/features', ['fetchFeatures']),
    ...mapActions('knowledgebase', ['getLimits']),

    getTakenNames (idx) {
      const names = []
      this.entity.children.forEach((entity, index) => {
        if (index !== idx) {
          names.push(entity.name)
        }
      })
      return names
    },

    updateRoles (roles) {
      this.machineLearningEntity.roles = roles.map(role => role.replace(/`/g, ''))
    },

    deleteChild (childPosition) {
      this.machineLearningEntity = {
        ...this.machineLearningEntity,
        children: [
          ...this.machineLearningEntity.children.slice(0, childPosition),
          ...this.machineLearningEntity.children.slice(childPosition + 1)
        ]
      }
    },

    addNewChildEntity () {
      this.machineLearningEntity = {
        ...this.machineLearningEntity,
        features: [],
        children: [
          ...this.machineLearningEntity.children,
          getDefaultChild(
            this.$t(
              'model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.childName',
              { childNumber: this.machineLearningEntity.children.length + 1 }
            )
          )
        ]
      }
    }
  }
}
</script>
